/**
 * Shared per-route ErrorBoundary used by storefront + admin child routes.
 *
 * The root ErrorBoundary in app/root.tsx already handles unhandled errors
 * with a full-page bilingual fallback. The benefit of a child-route
 * ErrorBoundary is **scope**: when only the inner content errors, the
 * surrounding layout (storefront nav / admin sidebar) stays mounted,
 * so users keep their navigation context + cart state instead of
 * watching the whole shell vanish.
 *
 * Sentry capture happens here too — `captureRemixErrorBoundaryError`
 * tags the breadcrumb with the route ID, giving better observability
 * than the root catch-all.
 *
 * Usage in a route file:
 *
 *   export { RouteErrorBoundary as ErrorBoundary } from "~/components/RouteErrorBoundary";
 *
 * Audit 2026-05-06: added during the launch-readiness sweep to cover
 * 12 routes that were missing local boundaries (inheriting only the
 * root catch-all).
 */
import { isRouteErrorResponse, useRouteError, Link } from "@remix-run/react";
import { captureRemixErrorBoundaryError } from "@sentry/remix";

export function RouteErrorBoundary() {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);

  const isRouteErr = isRouteErrorResponse(error);
  const status = isRouteErr ? error.status : 500;
  const is404 = status === 404;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 py-16 text-center"
    >
      <div className="mb-4 text-xs uppercase tracking-[0.18em] text-[#767676]">
        {status}
      </div>
      <h1 className="font-display text-2xl font-light text-luxury-charcoal mb-3">
        {is404 ? "Page not found" : "Something went wrong"}
      </h1>
      <p className="text-sm text-luxury-stone leading-relaxed mb-2">
        {is404
          ? "Trang bạn tìm kiếm không tồn tại hoặc đã bị xoá."
          : "Đã có lỗi xảy ra. Chúng tôi đã ghi nhận sự cố và đang xử lý."}
      </p>
      <p className="text-xs text-[#9a9a9a] mb-8">
        {is404
          ? "The page you're looking for doesn't exist or has been moved."
          : "We've logged the issue and are looking into it. Please try again, or contact us if you need immediate help."}
      </p>
      <div className="flex items-center justify-center gap-3">
        <Link to="/" className="btn-secondary inline-block text-xs uppercase">
          Back to home
        </Link>
        {!is404 && (
          <button
            type="button"
            onClick={() => {
              if (typeof window !== "undefined") window.location.reload();
            }}
            className="btn-primary inline-block text-xs uppercase"
          >
            Try again
          </button>
        )}
      </div>
    </div>
  );
}
